import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadSimple, X, Camera, FolderOpen, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * FileUpload component.
 * - Small non-video files → single-shot POST /api/upload.
 * - Video files → chunked resumable upload (10 MB chunks) via /api/upload/init,
 *   /api/upload/chunk, /api/upload/complete. Progress + resume are persisted to
 *   localStorage keyed by a stable file-identity hash, so if the user reloads the
 *   page mid-upload we pick up where we left off.
 *
 * Props:
 *  - accept: "image/*", "video/*", or "image/*,video/*"
 *  - value: current path (for preview)
 *  - onChange(path)
 *  - onUploaded(data): full response — for videos includes upload_id + processing status
 *  - label / testId
 */
const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB
const CHUNKED_THRESHOLD = 15 * 1024 * 1024; // any video (or file > 15MB) uses chunked path
const RESUME_KEY_PREFIX = "tain.upload.";
const MAX_CHUNK_RETRIES = 4;

function fileKey(file) {
  // Stable-ish client-side identity: name|size|lastModified. Not cryptographic — just
  // enough to detect "this is the same file the user picked earlier" for resume.
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function loadResume(key) {
  try {
    const raw = localStorage.getItem(RESUME_KEY_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveResume(key, data) {
  try {
    localStorage.setItem(RESUME_KEY_PREFIX + key, JSON.stringify(data));
  } catch {
    /* quota exceeded */
  }
}

function clearResume(key) {
  try {
    localStorage.removeItem(RESUME_KEY_PREFIX + key);
  } catch {
    /* ignore */
  }
}

export default function FileUpload({
  accept = "image/*",
  value,
  onChange,
  onUploaded,
  label = "Upload",
  testId = "file-upload",
}) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const abortRef = useRef({ cancelled: false });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(""); // "uploading" | "assembling" | "processing" | "ready"
  const [ct, setCt] = useState(null);
  const [pending, setPending] = useState(null); // { file, resume } — awaiting resume-or-restart choice

  useEffect(() => () => { abortRef.current.cancelled = true; }, []);

  // ---------------- Direct-to-Cloudinary upload (videos) ----------------
  // Videos bypass the backend entirely — the frontend fetches a short-lived
  // signed-upload params bundle from `/api/cloudinary/signature`, POSTs the
  // raw file straight to `api.cloudinary.com/v1_1/{cloud}/video/upload`,
  // then registers the resulting Cloudinary asset with the backend so the
  // existing post-creation flow can reference it via `upload_id`.
  const uploadDirectToCloudinary = async (file) => {
    setUploading(true);
    setPhase("uploading");
    setProgress(0);
    try {
      // 1. Ask backend for a signature (folder is server-controlled — user cannot exfiltrate).
      const isVideo = (file.type || "").startsWith("video/");
      const resourceType = isVideo ? "video" : "image";
      const { data: sig } = await api.get("/cloudinary/signature", {
        params: { resource_type: resourceType },
      });

      // 2. POST the file straight to Cloudinary with XHR (for progress events).
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const cloudResp = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (parseErr) { reject(parseErr); }
          } else {
            reject(new Error(`Cloudinary upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error("Network error uploading to Cloudinary"));
        xhr.onabort = () => reject(new Error("cancelled"));
        // Wire the cancel button.
        const abortWatcher = setInterval(() => {
          if (abortRef.current.cancelled) { try { xhr.abort(); } catch { /* ignore */ } clearInterval(abortWatcher); }
        }, 200);
        xhr.addEventListener("loadend", () => clearInterval(abortWatcher));
        xhr.send(form);
      });

      // 3. Register with backend so we get an upload_id + thumbnail derivative.
      setPhase("assembling");
      setProgress(100);
      const { data: reg } = await api.post("/upload/cloudinary/register", {
        secure_url: cloudResp.secure_url,
        public_id: cloudResp.public_id,
        resource_type: cloudResp.resource_type,
        format: cloudResp.format,
        duration: cloudResp.duration,
        width: cloudResp.width,
        height: cloudResp.height,
        bytes: cloudResp.bytes,
        original_filename: file.name,
      });

      setCt(reg.content_type);
      setPhase("ready");
      onChange(cloudResp.secure_url);
      onUploaded?.({
        upload_id: reg.upload_id,
        content_type: reg.content_type,
        status: "ready",
        chunked: false,
        cloudinary: true,
        secure_url: cloudResp.secure_url,
        thumbnail_path: reg.thumbnail_path,
        duration_seconds: reg.duration_seconds,
      });
      toast.success("Uploaded! Video is ready.");
    } catch (err) {
      if (err.message === "cancelled") {
        toast.info("Upload cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  // ---------------- Chunked upload pipeline (legacy fallback) ----------------
  const uploadChunked = async (file, existingUploadId = null, startFromReceived = []) => {
    setUploading(true);
    setPhase("uploading");
    setProgress(0);
    const key = fileKey(file);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    let uploadId = existingUploadId;
    let receivedSet = new Set(startFromReceived);

    try {
      if (!uploadId) {
        const initResp = await api.post("/upload/init", {
          filename: file.name,
          size: file.size,
          content_type: file.type || "application/octet-stream",
          chunk_size: CHUNK_SIZE,
          total_chunks: totalChunks,
          file_key: key,
        });
        uploadId = initResp.data.upload_id;
        receivedSet = new Set(initResp.data.received_chunks || []);
        if (initResp.data.resumed) {
          toast.success(`Resuming upload — ${receivedSet.size}/${totalChunks} chunks already uploaded`);
        }
        saveResume(key, { upload_id: uploadId, total_chunks: totalChunks, size: file.size });
      }

      const updateProgress = () => {
        setProgress(Math.round((receivedSet.size / totalChunks) * 100));
      };
      updateProgress();

      for (let i = 0; i < totalChunks; i++) {
        if (abortRef.current.cancelled) throw new Error("cancelled");
        if (receivedSet.has(i)) continue;

        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("chunk_index", String(i));
        form.append("file", blob, `${i}.bin`);

        let attempt = 0;
        // Exponential backoff retry for flaky mobile networks
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            await api.post("/upload/chunk", form, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 120000,
            });
            break;
          } catch (err) {
            attempt++;
            if (attempt > MAX_CHUNK_RETRIES) throw err;
            const wait = Math.min(500 * 2 ** attempt, 8000);
            await new Promise((r) => setTimeout(r, wait));
          }
        }
        receivedSet.add(i);
        saveResume(key, { upload_id: uploadId, total_chunks: totalChunks, size: file.size, received: [...receivedSet] });
        updateProgress();
      }

      setPhase("assembling");
      setProgress(100);
      const completeResp = await api.post("/upload/complete", { upload_id: uploadId });
      // Server returns upload_id + status ("queued"). Actual media_url comes async — we
      // pass the upload_id to onUploaded so the caller (CreatePost) can attach it to the post.
      const contentType = completeResp.data.content_type;
      setCt(contentType);
      setPhase("processing");
      // Path is not known yet — feed will pick it up once processing finishes.
      onChange(""); // no direct media path
      onUploaded?.({
        upload_id: uploadId,
        content_type: contentType,
        status: "processing",
        chunked: true,
      });
      clearResume(key);
      toast.success("Uploaded! Video is now processing in the background.");
    } catch (err) {
      if (err.message === "cancelled") {
        toast.info("Upload cancelled");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  // ---------------- Small-file single-shot path ----------------
  const uploadSingle = async (file) => {
    setUploading(true);
    setProgress(0);
    setPhase("uploading");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 240000,
      });
      setCt(data.content_type);
      onChange(data.path);
      onUploaded?.({ ...data, chunked: false, status: "ready" });
      toast.success("Uploaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startUpload = (file) => {
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    // Videos ALWAYS go direct-to-Cloudinary now — no chunking, no backend round-trip.
    if (isVideo) {
      uploadDirectToCloudinary(file);
      return;
    }
    // Small images use the single-shot backend path (kept for storage_path / R2).
    if (file.size <= CHUNKED_THRESHOLD) {
      uploadSingle(file);
      return;
    }
    // Anything else > threshold uses the chunked resumable path.
    const key = fileKey(file);
    const resume = loadResume(key);
    if (resume && resume.upload_id) {
      setPending({ file, resume });
      return;
    }
    uploadChunked(file);
  };

  const handleFile = (file) => {
    abortRef.current.cancelled = false;
    startUpload(file);
  };

  const acceptResume = async () => {
    if (!pending) return;
    const { file, resume } = pending;
    setPending(null);
    // Ask server for authoritative received-chunk list (defensive; server may have lost tmp).
    try {
      const { data } = await api.get(`/upload/status/${resume.upload_id}`);
      if (data.status === "ready") {
        // Already done — reuse
        onUploaded?.({
          upload_id: resume.upload_id,
          content_type: file.type,
          status: "ready",
          chunked: true,
        });
        clearResume(fileKey(file));
        toast.success("Upload already completed previously — reusing it.");
        return;
      }
      // Fetch fresh received list via re-init (server does resume-detection by file_key).
      uploadChunked(file, resume.upload_id, resume.received || []);
    } catch {
      // Session gone (server restart etc) — start fresh
      clearResume(fileKey(file));
      uploadChunked(file);
    }
  };

  const restartUpload = () => {
    if (!pending) return;
    const { file } = pending;
    clearResume(fileKey(file));
    setPending(null);
    uploadChunked(file);
  };

  const cancelUpload = () => {
    abortRef.current.cancelled = true;
  };

  const clear = () => {
    onChange("");
    setCt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const isVideo = ct?.startsWith("video") || accept.includes("video");
  const isImage = ct?.startsWith("image") || (!isVideo && accept.includes("image"));
  const previewUrl = value ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${value}` : null;
  const wantsVideo = accept.includes("video");
  const captureKind = wantsVideo ? "video/*" : "image/*";
  const showPreview = value && !uploading;

  const phaseLabel = {
    uploading: `Uploading… ${progress}%`,
    assembling: "Finalising…",
    processing: "Processing in background",
    ready: "Ready",
  }[phase] || "";

  return (
    <div className="space-y-2">
      <label className="text-sm text-white/70">{label}</label>
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={captureKind}
        capture="environment"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-camera-input`}
      />

      {/* Resume prompt */}
      {pending && !uploading && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 space-y-2" data-testid={`${testId}-resume-prompt`}>
          <p className="text-sm text-yellow-200">
            You have an unfinished upload for <span className="font-semibold">{pending.file.name}</span>.
            Resume where you left off?
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={acceptResume}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid={`${testId}-resume-accept`}
            >
              <ArrowClockwise size={14} weight="bold" /> Resume
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={restartUpload}
              className="border-white/20 bg-transparent hover:bg-white/5"
              data-testid={`${testId}-resume-restart`}
            >
              Start over
            </Button>
          </div>
        </div>
      )}

      {!showPreview && !pending && (
        <div className="space-y-2">
          <div className="w-full min-h-[140px] rounded-lg border-2 border-dashed border-white/15 bg-white/5 flex flex-col items-center justify-center gap-3 p-4">
            {uploading ? (
              <>
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <div className="w-full max-w-xs">
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-white/60 mt-2">
                    <span data-testid={`${testId}-progress`}>{phaseLabel}</span>
                    <button
                      type="button"
                      onClick={cancelUpload}
                      className="text-red-400 hover:underline"
                      data-testid={`${testId}-cancel`}
                    >
                      Cancel
                    </button>
                  </div>
                  {phase === "processing" && (
                    <p className="text-[11px] text-white/40 mt-2 text-center">
                      Your video is being optimized to 1080p. You can post it now — it'll go live once ready.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <UploadSimple size={28} weight="bold" className="text-white/50" />
                <span className="text-sm font-semibold text-white/70">
                  Choose {wantsVideo ? "video" : "image"} source
                </span>
                <div className="grid grid-cols-2 gap-2 w-full max-w-sm mt-1">
                  <Button
                    type="button"
                    data-testid={`${testId}-camera-trigger`}
                    onClick={() => cameraInputRef.current?.click()}
                    className="bg-blue-600 hover:bg-blue-700 gap-2 h-11 font-heading font-bold"
                  >
                    <Camera size={18} weight="fill" />
                    {wantsVideo ? "Record" : "Camera"}
                  </Button>
                  <Button
                    type="button"
                    data-testid={`${testId}-trigger`}
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="border-white/20 bg-transparent hover:bg-white/5 gap-2 h-11 font-heading font-bold"
                  >
                    <FolderOpen size={18} weight="fill" />
                    Gallery
                  </Button>
                </div>
                <span className="text-xs text-white/40 mt-1">
                  {wantsVideo ? "Videos up to 1 GB · chunked & resumable" : "Max 50MB"}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {showPreview && (
        <div className="relative rounded-lg overflow-hidden bg-black">
          {isVideo ? (
            <video
              src={previewUrl}
              className="w-full max-h-64 object-contain"
              controls
              muted
            />
          ) : (
            <img
              src={previewUrl}
              alt=""
              className="w-full max-h-64 object-contain"
            />
          )}
          <Button
            type="button"
            data-testid={`${testId}-remove`}
            size="sm"
            onClick={clear}
            className="absolute top-2 right-2 h-8 w-8 p-0 bg-red-600 hover:bg-red-700"
          >
            <X size={14} weight="bold" />
          </Button>
        </div>
      )}
    </div>
  );
}
